import { CbhIcon, Input, Text, TextArea, Title } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { useScrollToViewOnKeyboard } from "@src/appV2/redesign/utils/useScrollToViewOnKeyboard";
import { useRef } from "react";
import { type Control, Controller } from "react-hook-form";

import { type CreateSentHomeRequestFormValues } from "./types";

interface CreateSentHomeRequestFormProps {
  control: Control<CreateSentHomeRequestFormValues>;
  isSubmitting: boolean;
  isValid: boolean;
  onSubmit: () => void;
}

export function CreateSentHomeRequestForm(props: CreateSentHomeRequestFormProps) {
  const { control, isSubmitting, isValid, onSubmit } = props;

  const inputRef = useRef<HTMLTextAreaElement>(null);
  useScrollToViewOnKeyboard(inputRef);

  return (
    <Stack
      spacing={4}
      sx={{
        backgroundColor: (theme) => theme.palette.background.primary,
        paddingX: 5,
        paddingY: 6,
      }}
    >
      <Title semibold variant="h6" component="h6">
        Share why you were sent home
      </Title>

      <Text variant="body2" sx={{ color: (theme) => theme.palette.text.tertiary }}>
        Please describe in detail why you were sent home, including the name of the workplace
        representative (required)
      </Text>

      <Controller
        name="workplaceRepresentativeName"
        control={control}
        render={({ field }) => <Input {...field} placeholder="Workplace Representative Name" />}
      />

      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <TextArea {...field} inputRef={inputRef} rows={6} placeholder="Detailed description" />
        )}
      />

      <Text
        variant="body2"
        sx={(theme) => ({
          color: theme.palette.text.secondary,
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        })}
      >
        <CbhIcon type="info" size="small" sx={{ color: (theme) => theme.palette.icon?.primary }} />
        This message will be shared with the workplace
      </Text>

      <Box sx={{ paddingTop: 5 }}>
        <Button
          fullWidth
          sx={{
            "&.Mui-disabled": {
              backgroundColor: (theme) => theme.palette.grey[300],
              color: (theme) => theme.palette.grey[600],
            },
          }}
          type="submit"
          size="large"
          variant="contained"
          disabled={isSubmitting || !isValid}
          onClick={onSubmit}
        >
          Request workplace verification
        </Button>
      </Box>
    </Stack>
  );
}
